import { merchants as seedMerchants } from '@/mocks/merchants';
import type { Merchant } from '@/types/portal';

/**
 * Local persistence for the merchants a finance user pays.
 *
 * The seed directory ships the agency's common merchants with a few already
 * pinned; the pinned state (and any edits) is stored on top of it in
 * localStorage so merchant favourites survive between sessions. Falls back to
 * the seed set on first load or when storage is unavailable.
 */
const STORAGE_KEY = 'twiva.merchants.v1';

/** Read the persisted merchants, falling back to the seed set. */
export function loadMerchants(): Merchant[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return seedMerchants;
    const parsed = JSON.parse(raw) as Merchant[];
    if (!Array.isArray(parsed) || parsed.length === 0) return seedMerchants;
    return parsed;
  } catch {
    return seedMerchants;
  }
}

/** Persist the merchants; degrades quietly if storage is blocked. */
export function persistMerchants(merchants: Merchant[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merchants));
  } catch {
    // Storage unavailable (private mode) — the session keeps the in-memory copy.
  }
}

/** Flip a merchant's pinned state, returning a new list. */
export function toggleMerchantFavourite(merchants: Merchant[], id: string): Merchant[] {
  return merchants.map((merchant) =>
    merchant.id === id ? { ...merchant, favourite: !merchant.favourite } : merchant,
  );
}

/** Build a two-letter mark from a merchant name, falling back to the till. */
function buildMark(name: string, till: string): string {
  const initials = name
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return initials || till.slice(0, 2);
}

/** Build a new saved merchant from a name and till. New merchants start unpinned. */
export function createMerchant(name: string, till: string): Merchant {
  const trimmedName = name.trim() || `Till ${till}`;
  return {
    id: `merchant-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    name: trimmedName,
    mark: buildMark(trimmedName, till),
    till,
    category: 'Saved merchant',
    favourite: false,
  };
}